import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sparkles, Lightbulb, Upload, Users, Heart } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { GoogleLogin } from '@/components/auth/GoogleLogin';
import { FacebookLogin } from '@/components/auth/FacebookLogin';

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Handle messages from signup redirect
  useEffect(() => {
    const message = searchParams.get('message');
    if (message === 'account_created_check_email') {
      toast.info('Account created! Please check your email for confirmation, then sign in here.', {
        duration: 8000
      });
    } else if (message === 'check_email_confirmation') {
      toast.success('Please check your email and click the confirmation link, then sign in here.', {
        duration: 8000
      });
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await login({
        email: formData.email,
        password: formData.password,
        name: formData.email.split('@')[0],
      });
      toast.success('Successfully signed in!');
      
      // Check for redirect parameter, default to profile page if none
      const redirectTo = searchParams.get('redirect') || '/profile';
      navigate(redirectTo);
    } catch (error) {
      toast.error('Failed to sign in. Please check your credentials and try again.');
      console.error('Signin error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-8 lg:px-12">
        <div className="max-w-lg w-full space-y-10">
          <div className="text-center space-y-4">
            <h2 className="text-4xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-lg text-muted-foreground">
              Sign in to your account to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-2 gap-6">
              <div className="col-span-1">
                <GoogleLogin />
              </div>
              <div className="col-span-1">
                <FacebookLogin />
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-border"></div>
              </div>
              <div className="relative flex justify-center text-base">
                <span className="px-4 bg-background text-muted-foreground font-medium">
                  Or continue with email
                </span>
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <Input
                  type="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12 text-base"
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="h-12 text-base"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold"
              disabled={isLoading}
              size="lg"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
            <div className="text-center">
              <p className="text-base text-muted-foreground">
                Don't have an account?{' '}
                <Button
                  variant="link"
                  className="text-primary font-semibold p-0 h-auto"
                  onClick={() => navigate('/signup')}
                >
                  Sign up
                </Button>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Right Side - Benefits */}
      <div className="hidden lg:flex flex-1 bg-muted items-center justify-center p-16">
        <div className="max-w-lg space-y-12">
          <h3 className="text-3xl font-bold tracking-tight">
            Join our growing community
          </h3>
          <div className="space-y-10">
            <div className="flex items-start space-x-5">
              <div className="bg-background p-3 rounded-full border-2 border-border">
                <Sparkles className="h-7 w-7" />
              </div>
              <div className="space-y-2">
                <h4 className="text-xl font-semibold">Discover Amazing Displays</h4>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Browse through unique and creative light displays
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-5">
              <div className="bg-background p-3 rounded-full border-2 border-border">
                <Upload className="h-7 w-7" />
              </div>
              <div className="space-y-2">
                <h4 className="text-xl font-semibold">Share Your Creations</h4>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Upload and showcase your own light displays
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-5">
              <div className="bg-background p-3 rounded-full border-2 border-border">
                <Users className="h-7 w-7" />
              </div>
              <div className="space-y-2">
                <h4 className="text-xl font-semibold">Connect with Others</h4>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Join a community of light display enthusiasts
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-5">
              <div className="bg-background p-3 rounded-full border-2 border-border">
                <Heart className="h-7 w-7" />
              </div>
              <div className="space-y-2">
                <h4 className="text-xl font-semibold">Save Your Favorites</h4>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Bookmark displays you love for future reference
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 